import { emptyFeedbackDocument, parseFeedbackDocument, type FeedbackDocument, type FeedbackStorage } from '@cerebro-diem/core';

export function createFeedbackStorage(database = 'cerebro-feedback'): FeedbackStorage {
  const open = () => new Promise<IDBDatabase>((resolve, reject) => {
    let failed = false;
    try {
      const request = indexedDB.open(database, 1);
      request.onupgradeneeded = () => request.result.createObjectStore('authors');
      request.onerror = () => { failed = true; reject(new Error('Feedback storage could not be opened; existing reports have been kept')); };
      request.onblocked = () => { failed = true; reject(new Error('Feedback storage is blocked by another page')); };
      request.onsuccess = () => {
        if (failed) { request.result.close(); return; }
        request.result.onversionchange = () => request.result.close();
        resolve(request.result);
      };
    } catch { reject(new Error('Feedback storage is unavailable; your input is still here')); }
  });
  async function transaction(ownerId: string, change?: (doc: FeedbackDocument) => FeedbackDocument) {
    const db = await open();
    try {
      return await new Promise<FeedbackDocument>((resolve, reject) => {
        let reason: unknown;
        let result: FeedbackDocument;
        const tx = db.transaction('authors', change ? 'readwrite' : 'readonly', { durability: 'strict' });
        tx.oncomplete = () => resolve(result);
        tx.onabort = () => reject(reason ?? new Error('Feedback storage could not commit; existing reports have been kept and your input is still here'));
        tx.onerror = () => { reason ??= new Error('Feedback storage could not commit; it may be full or unavailable. Your input is still here'); };
        const store = tx.objectStore('authors');
        const request = store.get(ownerId);
        request.onsuccess = () => {
          try {
            result = request.result === undefined ? emptyFeedbackDocument() : parseFeedbackDocument(request.result, ownerId);
            if (change) {
              result = change(result);
              const raw = JSON.stringify(result);
              parseFeedbackDocument(raw, ownerId);
              store.put(raw, ownerId);
            }
          } catch (error) { reason = error; tx.abort(); }
        };
      });
    } finally { db.close(); }
  }
  return { read: ownerId => transaction(ownerId), mutate: transaction };
}
export const feedbackStorage = createFeedbackStorage();
